import { logger } from '@heidi/runtime/logger';
import type { Source } from '../../contracts/answers';
import { resolveMimsHtmlEmbed } from '../../contracts/mimsSourceEmbed';

// ===== SOURCE PARSING =====

// Exported for the Evidence sub-agent citation normalizer — this single-source
// parser carries the same partner-branding rules `parseSources` does; reuse it
// rather than forking per-source mapping.
export function parseSource(
  source: unknown,
  messageId: string | null | undefined,
  backendIndex: number
): Source | null {
  if (typeof source === 'string') {
    return { type: 'url', url: source };
  }

  if (typeof source !== 'object' || source === null) {
    logger.warn('Unknown source format in chat message', {
      source,
      backendIndex,
      messageId,
    });
    return null;
  }

  const sourceObj = source as Record<string, unknown>;

  // Handle user_document type sources (library documents)
  if (sourceObj.type === 'user_document' || sourceObj.document_id) {
    return {
      type: 'user_document',
      url: (sourceObj.url as string) || '',
      title: sourceObj.title as string | undefined,
      document_id: sourceObj.document_id as string | undefined,
      snippet: sourceObj.snippet as string | undefined,
      // EVI-1592: backend now streams an array (`collections: string[]`) so a
      // document or domain can belong to multiple user collections. Tolerate
      // the legacy singular `collection_name` field for backward-compat with
      // any older persisted message payloads still in flight.
      collections:
        (sourceObj.collections as string[] | undefined) ??
        (sourceObj.collection_name ? [sourceObj.collection_name as string] : undefined),
    };
  }

  // EVI-590: Handle integration_partner type sources
  // Preserve partner metadata for frontend detection and branding
  if (sourceObj.type === 'integration_partner') {
    return {
      type: 'integration_partner',
      url: (sourceObj.url as string) || '',
      title: sourceObj.title as string | undefined,
      snippet: sourceObj.snippet as string | undefined,
      display_summary: sourceObj.display_summary as string | undefined,
      source_type: 'integration_partner',
      source_id: sourceObj.source_id as string | undefined,
      partner_type: sourceObj.partner_type as string | undefined,
      display_name: sourceObj.display_name as string | undefined,
      domain: sourceObj.domain as string | undefined,
      embed: resolveMimsHtmlEmbed(
        sourceObj.embed as Source['embed'],
        sourceObj.partner_type as string | undefined,
        sourceObj.url as string | undefined
      ),
      // EVI-1105: per-citation branding persisted on reload (flat snake_case,
      // sibling to partner_type). Without these, reloaded chats fall back to the
      // partner-level (neutral) logo instead of the zone-specific one.
      logo_url: sourceObj.logo_url as string | undefined,
      brand_color: sourceObj.brand_color as string | undefined,
      figures: sourceObj.figures as Source['figures'],
    };
  }

  // Handle URL-based sources (web search)
  if ('url' in sourceObj && typeof sourceObj.url === 'string') {
    const title = typeof sourceObj.title === 'string' ? sourceObj.title : undefined;
    return {
      type: 'url',
      url: sourceObj.url,
      ...(title && { title }),
    };
  }

  logger.warn('Unknown source format in chat message', {
    source,
    backendIndex,
    messageId,
  });
  return null;
}

// Exported for the Projects persisted-evidence builder (AGE-493): source
// parsing carries citation-branding rules (EVI-590 / EVI-1105) that must
// not be forked.
export function parseSources(
  sources: Array<Record<string, unknown>> | null | undefined,
  messageId: string | null | undefined,
  backendIndex: number
): Source[] {
  if (!sources) return [];

  return (
    sources
      .map((source) => parseSource(source, messageId, backendIndex))
      // Accept sources with URL (web) OR document_id (library)
      .filter(
        (source): source is Source =>
          source !== null && (!!source.url || !!source.document_id)
      )
  );
}
